import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, RotateCcw, Clock, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { systemHealthService, RETRYABLE_JOB_TYPES } from '../../services/systemHealthService';
import type { IntegrationJob, JobType, JobStatus } from '../../services/systemHealthService';

const JOB_TYPE_LABELS: Record<JobType, string> = {
  send_email: 'Envio de email',
  analyze_lead: 'Análisis comercial',
  sync_calendar: 'Sincronizacion calendario',
  backup: 'Copia de seguridad',
  other: 'Otro',
};

const STATUS_CONFIG: Record<JobStatus, { label: string; badge: string; icon: React.ReactNode }> = {
  pending: {
    label: 'Pendiente',
    badge: 'bg-blue-50 text-blue-700 border-blue-100',
    icon: <Clock className="w-3 h-3" />,
  },
  in_progress: {
    label: 'En progreso',
    badge: 'bg-amber-50 text-amber-700 border-amber-100',
    icon: <Loader2 className="w-3 h-3 animate-spin" />,
  },
  completed: {
    label: 'Completado',
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    icon: <CheckCircle className="w-3 h-3" />,
  },
  failed: {
    label: 'Fallido',
    badge: 'bg-red-50 text-red-700 border-red-100',
    icon: <AlertTriangle className="w-3 h-3" />,
  },
};

function formatRelative(ts: string | null): string {
  if (!ts) return '—';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  return `Hace ${hours}h`;
}

interface JobRowProps {
  job: IntegrationJob;
  onRetry: (jobId: string) => void;
  retrying: boolean;
}

function JobRow({ job, onRetry, retrying }: JobRowProps) {
  const sc = STATUS_CONFIG[job.status];
  const isRetryable = RETRYABLE_JOB_TYPES.includes(job.job_type);

  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 text-sm text-gray-700">
        {JOB_TYPE_LABELS[job.job_type] ?? job.job_type}
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${sc.badge}`}>
          {sc.icon}
          {sc.label}
        </span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500 text-center">{job.attempts}</td>
      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
        {formatRelative(job.last_attempt_at)}
      </td>
      <td className="px-4 py-3 text-xs text-red-600 max-w-xs">
        {job.error_message ? (
          <details><summary>No se pudo completar. Ver detalle técnico</summary><span className="break-words">{job.error_message}</span></details>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {job.status === 'failed' && (
          <button
            onClick={() => onRetry(job.id)}
            disabled={retrying || !isRetryable}
            title={!isRetryable ? `Las tareas "${job.job_type}" requieren revisión manual` : 'Reintentar'}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg border transition-colors ${
              isRetryable
                ? 'text-gray-700 bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                : 'text-gray-400 bg-gray-50 border-gray-100 cursor-not-allowed opacity-60'
            }`}
          >
            <RotateCcw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} />
            {isRetryable ? "Reintentar" : "Revisión manual"}
          </button>
        )}
      </td>
    </tr>
  );
}

interface SectionProps {
  title: string;
  jobs: IntegrationJob[];
  onRetry: (jobId: string) => void;
  retryingId: string | null;
  emptyMessage: string;
}

function JobsSection({ title, jobs, onRetry, retryingId, emptyMessage }: SectionProps) {
  if (jobs.length === 0) {
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
        <p className="text-xs text-gray-400 py-3">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 mb-2">
        {title} <span className="font-normal text-gray-400">({jobs.length})</span>
      </h3>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Tipo</th>
              <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Estado</th>
              <th className="px-4 py-2.5 text-xs font-medium text-gray-500 text-center">Intentos</th>
              <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Ultimo intento</th>
              <th className="px-4 py-2.5 text-xs font-medium text-gray-500">Error</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-50">
            {jobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                onRetry={onRetry}
                retrying={retryingId === job.id}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const IntegrationJobsPanel: React.FC<{ refreshVersion?: number; onChanged?: () => void }> = ({ refreshVersion = 0, onChanged }) => {
  const [error, setError] = useState(false);
  const [jobs, setJobs] = useState<IntegrationJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);

  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    setError(false);
    try {
      const data = await systemHealthService.getIntegrationJobs();
      setJobs(data);
    } catch {
      setJobs([]);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs, refreshVersion]);

  const handleRetry = async (jobId: string) => {
    setRetryingId(jobId);
    try {
      const result = await systemHealthService.retryJob(jobId);
      setToast({ message: result.message, success: result.success });
      await loadJobs();
      onChanged?.();
    } catch (err) {
      setToast({ message: 'Error inesperado al reintentar', success: false });
    } finally {
      setRetryingId(null);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const pending = jobs.filter((j) => j.status === 'pending');
  const inProgress = jobs.filter((j) => j.status === 'in_progress');
  const failed = jobs.filter((j) => j.status === 'failed');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800">Tareas automáticas</h2>
        <button
          onClick={loadJobs}
          disabled={isLoading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      <p className="text-xs text-gray-500">Hasta 100 tareas pendientes, en curso o fallidas. Solo el envío de correo admite reintento desde aquí; las demás tareas requieren revisión manual.</p>
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : error ? <p role="alert" className="text-red-700">No se pudieron consultar las tareas. Pulsa Actualizar para reintentarlo.</p> : (
        <div className="space-y-6">
          <JobsSection
            title="Fallidos"
            jobs={failed}
            onRetry={handleRetry}
            retryingId={retryingId}
            emptyMessage="Sin tareas fallidas"
          />
          <JobsSection
            title="En progreso"
            jobs={inProgress}
            onRetry={handleRetry}
            retryingId={retryingId}
            emptyMessage="Sin tareas en progreso"
          />
          <JobsSection
            title="Pendientes"
            jobs={pending}
            onRetry={handleRetry}
            retryingId={retryingId}
            emptyMessage="Sin tareas pendientes"
          />
        </div>
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg z-50 ${
            toast.success ? 'bg-emerald-700' : 'bg-red-700'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default IntegrationJobsPanel;
